import CloseIcon from '@mui/icons-material/Close';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Box, Drawer, IconButton } from '@mui/material';

/**
 * MUI Drawer with imperative open/close via ref.
 *
 * @example
 * const drawerRef = useRef(null);
 * <TechnicianDetailsDrawer ref={drawerRef}>…</TechnicianDetailsDrawer>
 * drawerRef.current?.open();
 * drawerRef.current?.close();
 */
const TechnicianDetailsDrawer = forwardRef(
  ({ children, anchor = 'right', onClose, sx, PaperProps, ...rest }, ref) => {
    const [open, setOpen] = useState(false);

    const handleClose = useCallback(
      (event, reason) => {
        setOpen(false);
        onClose?.(event, reason);
      },
      [onClose],
    );

    useImperativeHandle(
      ref,
      () => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
      }),
      [],
    );

    return (
      <Drawer
        {...rest}
        hideBackdrop
        anchor={anchor}
        open={open}
        onClose={handleClose}
        sx={sx}
        PaperProps={PaperProps}>
        <Box
          sx={{
            width: 880,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
          role="presentation">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton
              aria-label="Close"
              edge="end"
              onClick={event => handleClose(event, 'closeClick')}
              size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>{children}</Box>
        </Box>
      </Drawer>
    );
  },
);

export default TechnicianDetailsDrawer;
